/*
  W4 step 2 — native Klaviyo subscription helper (doc #33 §W4, handoff 2026-09-02).

  Why: Klaviyo's forms renderer (~175 KB compressed / ~14 requests: signup_forms,
  Render, ClientStore, the onsite-back-in-stock chunks, styles, full-forms, geo-ip,
  company-fonts) loads on EVERY page for as long as the account has any live form —
  whether or not the page shows one. Replacing the last two embeds with native markup
  is what lets those forms go to Draft, and only then do the bytes leave.

  This posts to Klaviyo's public client subscriptions endpoint: public-key auth,
  CORS-enabled, designed to be called from a browser. Adding to list W5Eva7 produces
  "Added to List", which is what triggers the welcome series VcbHgf — exactly as the
  embedded forms did. No flows trigger on Submitted Form or Back In Stock Form
  Submitted, so nothing else depends on the form path.

  Contract:  window.riversolKlaviyoSubscribe({ email, source, properties }) -> Promise
  Resolves on 2xx (the endpoint returns 202 with an empty body). Throws otherwise.
*/
(function (global) {
  'use strict';

  // Rendered from more than one place (footer, OOS modal). The delegated submit
  // listener below is document-level, so running twice would double-fire it.
  if (global.riversolKlaviyoSubscribe) return;

  var COMPANY_ID = 'TMwKL4';
  var LIST_ID = 'W5Eva7'; // RIVERSOL EMAIL MASTER LIST (single opt-in)
  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + COMPANY_ID;
  var REVISION = '2025-07-15';

  // Deliberately permissive: Klaviyo validates properly server-side, and a strict
  // client regex rejects valid addresses (plus-addressing, long TLDs, unicode).
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function subscribe(options) {
    var opts = options || {};
    var email = String(opts.email || '').trim();

    if (!EMAIL_RE.test(email)) {
      return Promise.reject(new Error('invalid-email'));
    }

    var profileAttrs = { email: email };
    profileAttrs.subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
    if (opts.properties && typeof opts.properties === 'object') {
      profileAttrs.properties = opts.properties;
    }

    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: opts.source || 'Riversol theme (native)',
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        },
        relationships: { list: { data: { type: 'list', id: LIST_ID } } }
      }
    };

    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify(body),
      keepalive: true
    }).then(function (res) {
      if (res.ok) return;
      // JSON:API error body on 4xx — surface the first detail for the console,
      // never to the customer.
      return res.json().catch(function () { return null; }).then(function (j) {
        var detail = j && j.errors && j.errors[0] && j.errors[0].detail;
        throw new Error('klaviyo-' + res.status + (detail ? ': ' + detail : ''));
      });
    });
  }

  global.riversolKlaviyoSubscribe = subscribe;

  /* ---- Delegated wiring for <form data-klaviyo-subscribe> -------------------
     Delegated so forms rendered later (drawers, modals, section re-renders) work
     without rebinding. The OOS notify form has its own handler — it needs the
     product payload and the Back In Stock event — so it opts out with
     data-klaviyo-subscribe="manual".                                          */

  // Copy lives on the <form> as data-* attributes (set in
  // snippets/klaviyo-native-form.liquid) so callers can override it in Liquid;
  // the strings here are only the fallback if an attribute is missing.
  function attr(form, name, fallback) {
    var v = form.getAttribute(name);
    return v && v.trim() ? v : fallback;
  }

  function setMessage(form, title, body, isError) {
    var el = form.querySelector('[data-message]');
    if (!el) return;
    var t = el.querySelector('[data-message-title]');
    var b = el.querySelector('[data-message-body]');
    if (t) t.textContent = title || '';
    if (b) { b.textContent = body || ''; b.hidden = !body; }
    el.classList.toggle('newsletter-form__message--error', !!isError);
    el.classList.toggle('newsletter-form__message--success', !isError);
    el.hidden = false;
  }

  // Revised 2026-09-15 (Tomás's PR #66 review). Success replaces the field and
  // button in place with the live embed's "Thank you for subscribing!": the
  // wrapper gets a state class (CSS hides the field row — a class, not the
  // hidden attribute, because the theme's own display rules on that row would
  // out-rank the UA [hidden] style), the confirmation fills the slot, and focus
  // moves onto it so keyboard users are not left on a control that just vanished.
  function showSuccess(form) {
    var wrapper = form.closest ? form.closest('.newsletter-form') : null;
    if (wrapper) wrapper.classList.add('newsletter-form--success');
    setMessage(
      form,
      attr(form, 'data-success-title', 'Thank you for subscribing!'),
      attr(form, 'data-success-body', ''),
      false
    );
    form.reset();
    var el = form.querySelector('[data-message]');
    if (el && el.focus) { try { el.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
  }

  function showError(form, text) {
    setMessage(form, text, '', true);
  }

  global.document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('form[data-klaviyo-subscribe]');
    if (!form) return;
    if (form.getAttribute('data-klaviyo-subscribe') === 'manual') return;

    event.preventDefault();
    if (form.dataset.pending === '1') return;

    // Honeypot — a bot filled a field no human can see. Show the same success
    // state so the bot learns nothing from the response.
    var trap = form.querySelector('input[name="company_website"]');
    if (trap && trap.value) { showSuccess(form); return; }

    var input = form.querySelector('input[type="email"]');
    var button = form.querySelector('[data-submit]');
    var email = input ? input.value : '';

    // Clear a previous error before re-validating.
    var prior = form.querySelector('[data-message]');
    if (prior && prior.classList.contains('newsletter-form__message--error')) prior.hidden = true;

    form.dataset.pending = '1';
    if (button) button.setAttribute('aria-busy', 'true');

    subscribe({ email: email, source: form.getAttribute('data-source') || undefined })
      .then(function () {
        showSuccess(form);
      })
      .catch(function (err) {
        showError(
          form,
          String(err && err.message) === 'invalid-email'
            ? attr(form, 'data-error-invalid', 'Please enter a valid email address.')
            : attr(form, 'data-error-generic', 'Something went wrong on our side. Please try again in a moment.')
        );
        if (input && String(err && err.message) === 'invalid-email') { try { input.focus(); } catch (e) { /* noop */ } }
        if (window.console && console.warn) console.warn('[klaviyo-subscribe]', err);
      })
      .then(function () {
        form.dataset.pending = '';
        if (button) button.removeAttribute('aria-busy');
      });
  });
})(window);
